'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useCustomRouter } from '@/hooks/useCustomRouter';

import {
  parseEnumValue,
  parseStringList,
  toggleArrayItem,
} from '../utils';

export type PolicySortOption = 'POPULARITY' | 'LATEST';

export interface PolicyFilterState {
  keyword: string;
  targetCodes: string[];
  provisionCodes: string[];
  jobCodes: string[];
  ageCodes: string[];
  sort: PolicySortOption;
  page: number;
}

const SORT_OPTIONS: readonly PolicySortOption[] = ['POPULARITY', 'LATEST'];

const DEFAULTS: PolicyFilterState = {
  keyword: '',
  targetCodes: [],
  provisionCodes: [],
  jobCodes: [],
  ageCodes: [],
  sort: 'POPULARITY',
  page: 1,
};

const PARAM_KEYS = {
  keyword: 'q',
  targetCodes: 'targetCodes',
  provisionCodes: 'provisionCodes',
  jobCodes: 'jobCodes',
  ageCodes: 'ageCodes',
  sort: 'sort',
  page: 'page',
} as const;

function parsePage(value: string | null): number {
  const num = Number(value);
  if (!Number.isFinite(num) || num < 1) return DEFAULTS.page;

  return Math.floor(num);
}

function parse(params: URLSearchParams): PolicyFilterState {
  return {
    keyword: params.get(PARAM_KEYS.keyword) ?? DEFAULTS.keyword,
    targetCodes: parseStringList(params.get(PARAM_KEYS.targetCodes)),
    provisionCodes: parseStringList(params.get(PARAM_KEYS.provisionCodes)),
    jobCodes: parseStringList(params.get(PARAM_KEYS.jobCodes)),
    ageCodes: parseStringList(params.get(PARAM_KEYS.ageCodes)),
    sort: parseEnumValue(
      params.get(PARAM_KEYS.sort),
      SORT_OPTIONS,
      DEFAULTS.sort,
    ),
    page: parsePage(params.get(PARAM_KEYS.page)),
  };
}

function setOrDelete(
  params: URLSearchParams,
  key: string,
  value: string | undefined,
) {
  if (value) params.set(key, value);
  else params.delete(key);
}

function write(params: URLSearchParams, state: PolicyFilterState) {
  setOrDelete(params, PARAM_KEYS.keyword, state.keyword);
  setOrDelete(
    params,
    PARAM_KEYS.targetCodes,
    state.targetCodes.length ? state.targetCodes.join(',') : undefined,
  );
  setOrDelete(
    params,
    PARAM_KEYS.provisionCodes,
    state.provisionCodes.length ? state.provisionCodes.join(',') : undefined,
  );
  setOrDelete(
    params,
    PARAM_KEYS.jobCodes,
    state.jobCodes.length ? state.jobCodes.join(',') : undefined,
  );
  setOrDelete(
    params,
    PARAM_KEYS.ageCodes,
    state.ageCodes.length ? state.ageCodes.join(',') : undefined,
  );
  setOrDelete(
    params,
    PARAM_KEYS.sort,
    state.sort !== DEFAULTS.sort ? state.sort : undefined,
  );
  setOrDelete(
    params,
    PARAM_KEYS.page,
    state.page !== DEFAULTS.page ? String(state.page) : undefined,
  );
}

type NavigateMethod = 'push' | 'replace';

export function usePolicyFilter() {
  const router = useCustomRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const paramsString = searchParams?.toString() ?? '';

  const [state, setState] = useState<PolicyFilterState>(() =>
    parse(new URLSearchParams(paramsString)),
  );

  // 외부 URL 변경(뒤로가기 등)이 일어나면 state로 반영
  useEffect(() => {
    const parsed = parse(new URLSearchParams(paramsString));
    setState((prev) =>
      JSON.stringify(prev) === JSON.stringify(parsed) ? prev : parsed,
    );
  }, [paramsString]);

  const pendingRef = useRef<{
    state: PolicyFilterState;
    method: NavigateMethod;
  } | null>(null);

  // state 변경 후 URL에 반영. updater 안에서 router를 호출하지 않으려고 effect로 분리.
  useEffect(() => {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;

    const params = new URLSearchParams(paramsString);
    write(params, pending.state);
    const qs = params.toString();
    const url = qs ? `${pathname}?${qs}` : pathname;
    if (pending.method === 'push') {
      router.push(url, { scroll: false });
    } else {
      router.replace(url, { scroll: false });
    }
  }, [state, pathname, paramsString, router]);

  const update = useCallback(
    (
      updater: (prev: PolicyFilterState) => PolicyFilterState,
      method: NavigateMethod = 'replace',
    ) => {
      setState((prev) => {
        const next = updater(prev);
        pendingRef.current = { state: next, method };

        return next;
      });
    },
    [],
  );

  const setKeyword = useCallback(
    (keyword: string) => {
      update((prev) => ({ ...prev, keyword, page: DEFAULTS.page }), 'push');
      window.scrollTo(0, 0);
    },
    [update],
  );

  const clearKeyword = useCallback(() => {
    update((prev) => ({ ...prev, keyword: '' }));
  }, [update]);

  const toggleTargetCode = useCallback(
    (code: string) => {
      update((prev) => ({
        ...prev,
        targetCodes: toggleArrayItem(prev.targetCodes, code),
        page: DEFAULTS.page,
      }));
    },
    [update],
  );

  const toggleProvisionCode = useCallback(
    (code: string) => {
      update((prev) => ({
        ...prev,
        provisionCodes: toggleArrayItem(prev.provisionCodes, code),
        page: DEFAULTS.page,
      }));
    },
    [update],
  );

  const toggleJobCode = useCallback(
    (code: string) => {
      update((prev) => ({
        ...prev,
        jobCodes: toggleArrayItem(prev.jobCodes, code),
        page: DEFAULTS.page,
      }));
    },
    [update],
  );

  const toggleAgeCode = useCallback(
    (code: string) => {
      update((prev) => ({
        ...prev,
        ageCodes: toggleArrayItem(prev.ageCodes, code),
        page: DEFAULTS.page,
      }));
    },
    [update],
  );

  const resetFilters = useCallback(() => {
    update((prev) => ({
      ...prev,
      targetCodes: [],
      provisionCodes: [],
      jobCodes: [],
      ageCodes: [],
      page: DEFAULTS.page,
    }));
  }, [update]);

  const setSort = useCallback(
    (sort: PolicySortOption) => {
      update((prev) => ({ ...prev, sort, page: DEFAULTS.page }));
    },
    [update],
  );

  const setPage = useCallback(
    (page: number) => {
      update((prev) => ({ ...prev, page }));
    },
    [update],
  );

  const activeFilterCount =
    state.targetCodes.length +
    state.provisionCodes.length +
    state.jobCodes.length +
    state.ageCodes.length;

  return {
    state,
    setKeyword,
    clearKeyword,
    toggleTargetCode,
    toggleProvisionCode,
    toggleJobCode,
    toggleAgeCode,
    resetFilters,
    setSort,
    setPage,
    activeFilterCount,
  };
}
